import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { MessageSquarePlus, X, Loader2, Send, Image as ImageIcon, Paperclip } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { FEEDBACK_FEATURES } from "@/lib/features";

// Beta-feedback categories — mirror backend `FEEDBACK_CATEGORIES`.
const CATEGORIES = [
  { id: "bug",               label: "Bug" },
  { id: "feature_request",   label: "Feature request" },
  { id: "ux_issue",          label: "UX issue" },
  { id: "positive_feedback", label: "Love it" },
  { id: "other",             label: "Other" },
];

// Floating "Feedback" button — visible on every page inside the app shell.
// Sends {category, feature, message, page, route, screenshot_path} to
// /api/feedback which also emails the admin.
export default function FeedbackWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("feature_request");
  const [feature, setFeature] = useState("");
  const [message, setMessage] = useState("");
  const [screenshotPath, setScreenshotPath] = useState("");
  const [screenshotName, setScreenshotName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showNudge, setShowNudge] = useState(false);
  const fileRef = useRef(null);
  const loc = useLocation();

  // First-visit nudge — a small speech-bubble hint pointing at the button
  // for users who haven't used feedback yet.
  useEffect(() => {
    if (!user) return;
    const key = `discrimina_feedback_seen_${user.id}`;
    if (localStorage.getItem(key)) return;
    const delay = setTimeout(() => setShowNudge(true), 3500);
    const auto = setTimeout(() => setShowNudge(false), 15500);
    return () => { clearTimeout(delay); clearTimeout(auto); };
  }, [user]);

  const dismissNudge = () => {
    setShowNudge(false);
    if (user) localStorage.setItem(`discrimina_feedback_seen_${user.id}`, "1");
  };

  const openModal = () => {
    dismissNudge();
    setOpen(true);
  };

  const resetForm = () => {
    setMessage("");
    setFeature("");
    setScreenshotPath("");
    setScreenshotName("");
    setCategory("feature_request");
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow selecting same file again after removal
    if (!f) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(f.type)) {
      toast.error("PNG, JPG, or WebP only");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Screenshot too large (5 MB max)");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await http.post("/feedback/screenshot", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setScreenshotPath(data.path || "");
      setScreenshotName(f.name);
      toast.success("Screenshot attached");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not upload screenshot");
    } finally {
      setUploading(false);
    }
  };

  const removeScreenshot = () => {
    setScreenshotPath("");
    setScreenshotName("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setBusy(true);
    try {
      await http.post("/feedback", {
        category,
        feature: feature || "",
        message: message.trim(),
        page: loc.pathname,
        route: loc.pathname,
        screenshot_path: screenshotPath || "",
      });
      toast.success("Thanks — your note reached the founder's inbox.");
      resetForm();
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send feedback");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="fixed z-40 bottom-24 right-4 md:bottom-6 md:right-6 flex flex-col items-end gap-2">
        {showNudge && (
          <div
            className="max-w-[240px] bg-white border border-slate-200 rounded-2xl shadow-2xl p-3 pr-8 text-xs text-slate-700 leading-snug relative animate-in fade-in slide-in-from-bottom-2 duration-500"
            data-testid="feedback-nudge"
          >
            <button
              type="button"
              onClick={dismissNudge}
              aria-label="Dismiss hint"
              className="absolute top-1.5 right-1.5 text-slate-400 hover:text-slate-700 p-0.5"
              data-testid="feedback-nudge-close"
            >
              <X className="w-3.5 h-3.5" />
            </button>
            <div className="font-semibold text-slate-900 mb-0.5">Something on your mind?</div>
            <div className="text-slate-600">
              Tap here anytime to tell me what to build, fix, or love. Every note reaches me directly.
            </div>
            <div className="absolute -bottom-1.5 right-6 w-3 h-3 bg-white border-r border-b border-slate-200 rotate-45"></div>
          </div>
        )}
        <button
          onClick={openModal}
          data-testid="feedback-open-button"
          aria-label="Send feedback"
          className="relative h-12 pl-4 pr-5 rounded-full bg-slate-900 text-white shadow-xl hover:shadow-2xl transition-shadow flex items-center gap-2 font-semibold text-sm"
        >
          <MessageSquarePlus className="w-4 h-4" />
          <span className="hidden sm:inline">Feedback</span>
          {showNudge && (
            <span className="absolute -top-1 -right-1 flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-sky-500"></span>
            </span>
          )}
        </button>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => !busy && setOpen(false)}
          data-testid="feedback-modal"
        >
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-2xl p-6 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-bold">Beta feedback</div>
                <div className="font-display text-xl font-extrabold mt-0.5">Help shape Discrimina</div>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700" data-testid="feedback-close-button">
                <X className="w-5 h-5" />
              </button>
            </div>

            <label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-bold">What is this?</label>
            <div className="flex flex-wrap gap-2 mt-1.5 mb-4" data-testid="feedback-category-row">
              {CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCategory(c.id)}
                  data-testid={`feedback-category-${c.id}`}
                  className={`px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${
                    category === c.id
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-bold">
              Which feature? <span className="text-slate-400 normal-case tracking-normal font-normal">(optional)</span>
            </label>
            <select
              value={feature}
              onChange={(e) => setFeature(e.target.value)}
              data-testid="feedback-feature-select"
              className="mt-1.5 mb-4 w-full h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">— No specific feature —</option>
              {FEEDBACK_FEATURES.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>

            <label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-bold">Tell us more</label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What would make this better? What's broken? What's missing?"
              rows={5}
              maxLength={2000}
              data-testid="feedback-message-input"
              className="rounded-xl resize-none mt-1.5"
              required
            />
            <div className="text-[11px] text-slate-400 mt-1 text-right">{message.length} / 2000</div>

            <div className="mt-3">
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleFile}
                className="hidden"
                data-testid="feedback-screenshot-input"
              />
              {!screenshotPath ? (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="feedback-screenshot-btn"
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
                  {uploading ? "Uploading…" : "Attach screenshot"}
                </button>
              ) : (
                <div
                  className="inline-flex items-center gap-2 h-8 px-3 rounded-full text-xs font-semibold border border-teal-200 bg-teal-50 text-teal-800"
                  data-testid="feedback-screenshot-chip"
                >
                  <ImageIcon className="w-3.5 h-3.5" />
                  <span className="max-w-[140px] truncate">{screenshotName || "screenshot"}</span>
                  <button
                    type="button"
                    onClick={removeScreenshot}
                    aria-label="Remove screenshot"
                    className="text-teal-700 hover:text-teal-900"
                    data-testid="feedback-screenshot-remove"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <div className="text-[11px] text-slate-400 mt-1">PNG, JPG or WebP · 5 MB max · no PHI, please.</div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="rounded-full" disabled={busy}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy || !message.trim()} className="rounded-full" data-testid="feedback-submit-button">
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Send</>}
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
